// "Last used" sign-in hint. Remembers, per device, which method a user last
// signed in with, so the sign-in page can badge that method and a returning
// user picks the same door instead of forking a second account.
//
// This is a pure client hint keyed off local storage. It never does a
// server-side account lookup and never touches the auth flow itself.

import { clearLastPhoneCountry } from './LastPhoneCountry';

export type LastLoginMethod = 'email' | 'google' | 'apple';

// Committed key: survives across sessions (localStorage), matching the
// existing `museflow:`-namespaced convention (see museflow:oauth-pending).
const LAST_LOGIN_METHOD_KEY = 'museflow:last-login-method';

// Pending OAuth intent: the provider the user is *attempting*, stashed at
// redirect time and promoted to the committed key only on confirmed success.
// sessionStorage (not localStorage) so an abandoned/failed OAuth dies with the
// tab and is never mistaken for "last used" — same lifetime reasoning as
// museflow:oauth-pending.
const OAUTH_METHOD_PENDING_KEY = 'museflow:last-login-method-pending';

const VALID_METHODS: readonly LastLoginMethod[] = ['email', 'google', 'apple'];

function isLastLoginMethod(value: unknown): value is LastLoginMethod {
  return typeof value === 'string' && (VALID_METHODS as readonly string[]).includes(value);
}

/** The method last used on this device, or null if none/invalid/unavailable. */
export function getLastLoginMethod(): LastLoginMethod | null {
  try {
    const raw = localStorage.getItem(LAST_LOGIN_METHOD_KEY);
    return isLastLoginMethod(raw) ? raw : null;
  } catch {
    // localStorage may be unavailable in some contexts.
    return null;
  }
}

/** Record the method after a confirmed successful sign-in. Overwrites. */
export function setLastLoginMethod(method: LastLoginMethod): void {
  try {
    localStorage.setItem(LAST_LOGIN_METHOD_KEY, method);
  } catch {
    // localStorage may be unavailable in some contexts.
  }
}

/**
 * Stash the OAuth provider the user is attempting, before the redirect. This
 * is intent only — it is NOT the committed "last used" value. It is promoted
 * to the committed key by commitOauthLoginMethod() on confirmed success, and
 * is otherwise discarded, so a failed or abandoned OAuth never records a
 * "last used" method.
 */
export function setPendingOauthMethod(method: 'google' | 'apple'): void {
  try {
    sessionStorage.setItem(OAUTH_METHOD_PENDING_KEY, method);
  } catch {
    // sessionStorage may be unavailable in some contexts.
  }
}

function readAndClearPendingOauthMethod(): 'google' | 'apple' | null {
  try {
    const raw = sessionStorage.getItem(OAUTH_METHOD_PENDING_KEY);
    sessionStorage.removeItem(OAUTH_METHOD_PENDING_KEY);
    return raw === 'google' || raw === 'apple' ? raw : null;
  } catch {
    return null;
  }
}

function normalizeProvider(name: unknown): 'google' | 'apple' | null {
  if (typeof name !== 'string') return null;
  const n = name.toLowerCase();
  if (n === 'google') return 'google';
  // Cognito's Apple provider name is "SignInWithApple"; also accept "apple".
  if (n === 'signinwithapple' || n === 'apple') return 'apple';
  return null;
}

/**
 * Derive the OAuth provider ('google' | 'apple') from the returned Cognito ID
 * token claims. Pure and defensive — returns null if the shape is
 * indeterminate, in which case the caller falls back to the stashed intent.
 *
 * Two signals, in priority order:
 *   1. the `identities` claim (array, or JSON string) → providerName
 *   2. the `cognito:username` claim, federated-prefixed (e.g. "Google_123",
 *      "SignInWithApple_123")
 */
export function deriveOauthMethodFromProfile(
  profile: Record<string, unknown> | undefined | null,
): 'google' | 'apple' | null {
  if (!profile) return null;

  // 1. identities claim
  let identities: unknown = profile.identities;
  if (typeof identities === 'string') {
    try {
      identities = JSON.parse(identities);
    } catch {
      identities = undefined;
    }
  }
  if (Array.isArray(identities) && identities.length > 0) {
    const first = identities[0] as Record<string, unknown> | undefined;
    const fromIdentities = normalizeProvider(first?.providerName);
    if (fromIdentities) return fromIdentities;
  }

  // 2. cognito:username federated prefix
  const cognitoUsername = profile['cognito:username'];
  if (typeof cognitoUsername === 'string') {
    const prefix = cognitoUsername.split('_', 1)[0];
    const fromUsername = normalizeProvider(prefix);
    if (fromUsername) return fromUsername;
  }

  return null;
}

/**
 * Promote a confirmed OAuth success into the committed "last used" value.
 * Prefers the token-derived provider; falls back to the stashed redirect
 * intent. Always clears the pending intent. No-op if neither yields a
 * provider (leaves any prior value untouched rather than guessing).
 */
export function commitOauthLoginMethod(profile: Record<string, unknown> | undefined | null): void {
  const derived = deriveOauthMethodFromProfile(profile);
  const intent = readAndClearPendingOauthMethod();
  const method = derived ?? intent;
  if (method) {
    setLastLoginMethod(method);
    // The last-used door is no longer a phone number.
    clearLastPhoneCountry();
  }
}
